/**
 * Fişi tarayıcıda ESC/POS baytlarına çevirir — Android tablette RawBT uygulamasıyla basmak için.
 * Düzen, kasa bilgisayarındaki yazdırma programıyla (yazici-kurulum/print-server.js) aynıdır;
 * birinde değişen fiş düzeni diğerinde de güncellenmeli.
 */

export interface ReceiptItem {
  name: string;
  qty: number;
  price: number;
  note?: string | null;
  extras?: { name: string; qty?: number; totalQty?: number; total?: number }[];
}

export interface ReceiptData {
  restaurantName?: string;
  orderId?: string;
  orderDate?: string;
  items: ReceiptItem[];
  total: number;
  discount?: number;
  paymentMethod?: string;
  customerName?: string;
  customerPhone?: string;
  address?: string;
  orderNote?: string | null;
  footer?: string;
  /** Fişin en altına QR kodu olarak basılır (adresin Google Haritalar linki) */
  mapUrl?: string;
}

export interface EscPosOptions {
  width: number;          // 48 (80 mm) | 32 (58 mm)
  charset: "tr" | "ascii";
  codePage: number;       // PC857 (Türkçe) = 13
  cut: boolean;
}

const ESC = 0x1b;
const GS = 0x1d;
const CMD = {
  init: [ESC, 0x40],
  alignLeft: [ESC, 0x61, 0],
  alignCenter: [ESC, 0x61, 1],
  boldOn: [ESC, 0x45, 1],
  boldOff: [ESC, 0x45, 0],
  sizeNormal: [GS, 0x21, 0x00],
  sizeDouble: [GS, 0x21, 0x11],
  sizeTall: [GS, 0x21, 0x01],
  feed: (n: number) => [ESC, 0x64, n],
  cut: [GS, 0x56, 0x42, 0x00],
  codePage: (n: number) => [ESC, 0x74, n],
};

/** ESC/POS yerleşik QR kodu (GS ( k) — kodu yazıcı kendisi çizer. moduleSize: kare başına nokta. */
function qrCode(data: string, moduleSize: number): number[] {
  const d = Array.from(new TextEncoder().encode(data));
  const len = d.length + 3;
  return [
    GS, 0x28, 0x6b, 4, 0, 0x31, 0x41, 0x32, 0x00,                 // model 2
    GS, 0x28, 0x6b, 3, 0, 0x31, 0x43, moduleSize,                  // boyut
    GS, 0x28, 0x6b, 3, 0, 0x31, 0x45, 0x31,                        // hata düzeltme: M
    GS, 0x28, 0x6b, len & 0xff, len >> 8, 0x31, 0x50, 0x30, ...d,  // veriyi yükle
    GS, 0x28, 0x6b, 3, 0, 0x31, 0x51, 0x30,                        // bas
    0x0a,
  ];
}

/** CP857 (Türkçe) */
const CP857: Record<string, number> = {
  "Ç": 0x80, "ü": 0x81, "é": 0x82, "â": 0x83, "ä": 0x84, "à": 0x85, "ç": 0x87, "ê": 0x88,
  "ë": 0x89, "è": 0x8a, "ï": 0x8b, "î": 0x8c, "ı": 0x8d, "Ä": 0x8e, "É": 0x90, "ô": 0x93,
  "ö": 0x94, "ò": 0x95, "û": 0x96, "ù": 0x97, "İ": 0x98, "Ö": 0x99, "Ü": 0x9a, "Ş": 0x9e,
  "ş": 0x9f, "á": 0xa0, "í": 0xa1, "ó": 0xa2, "ú": 0xa3, "ñ": 0xa4, "Ñ": 0xa5, "Ğ": 0xa6, "ğ": 0xa7,
};

const ASCII_FALLBACK: Record<string, string> = {
  "ç": "c", "Ç": "C", "ğ": "g", "Ğ": "G", "ı": "i", "İ": "I", "ö": "o", "Ö": "O",
  "ş": "s", "Ş": "S", "ü": "u", "Ü": "U", "₺": "TL", "–": "-", "—": "-", "’": "'", "“": "\"", "”": "\"",
  "·": "-", "…": "...", "×": "x",
};

function encode(text: string, charset: "tr" | "ascii"): number[] {
  const out: number[] = [];
  for (const ch of String(text ?? "")) {
    if (charset === "tr" && CP857[ch] !== undefined) { out.push(CP857[ch]); continue; }
    const fb = ASCII_FALLBACK[ch];
    if (fb !== undefined) { for (const c of fb) out.push(c.charCodeAt(0)); continue; }
    const code = ch.charCodeAt(0);
    out.push(code < 128 ? code : 0x3f);
  }
  return out;
}

function wrap(text: string, width: number): string[] {
  const words = String(text ?? "").split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = "";
  for (const w of words) {
    if (!cur) cur = w;
    else if ((cur + " " + w).length <= width) cur += " " + w;
    else { lines.push(cur); cur = w; }
    while (cur.length > width) { lines.push(cur.slice(0, width)); cur = cur.slice(width); }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [""];
}

function twoCol(left: string, right: string, width: number): string[] {
  const leftLines = wrap(left, Math.max(8, width - right.length - 1));
  const last = leftLines.pop() ?? "";
  const gap = Math.max(1, width - last.length - right.length);
  return [...leftLines, last + " ".repeat(gap) + right];
}

function money(n: number): string {
  return `${Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`;
}

export function buildReceiptBytes(r: ReceiptData, o: EscPosOptions): Uint8Array {
  const { width, charset } = o;
  const bytes: number[] = [];
  const push = (...arr: number[][]) => arr.forEach((a) => bytes.push(...a));
  const line = (t = "") => push(encode(t, charset), [0x0a]);
  const rule = (ch = "-") => line(ch.repeat(width));

  push(CMD.init);
  if (charset === "tr") push(CMD.codePage(o.codePage));

  push(CMD.alignCenter, CMD.boldOn, CMD.sizeDouble);
  wrap(r.restaurantName || "Mutfacık", Math.floor(width / 2)).forEach((l) => line(l));
  push(CMD.sizeNormal, CMD.boldOff);
  if (r.orderId) { push(CMD.boldOn); line(`Sipariş No: ${r.orderId}`); push(CMD.boldOff); }
  if (r.orderDate) line(new Date(r.orderDate).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }));
  push(CMD.alignLeft);
  rule("=");

  r.items.forEach((it) => {
    const qty = Number(it.qty || 1);
    push(CMD.boldOn);
    twoCol(`${qty}x ${it.name}`, money(qty * Number(it.price || 0)), width).forEach((l) => line(l));
    push(CMD.boldOff);
    (it.extras ?? []).forEach((ex) => {
      const label = `  + ${ex.name} x${ex.totalQty ?? ex.qty ?? 1}`;
      if (!ex.total) wrap(`${label} (Ücretsiz)`, width).forEach((l) => line(l));
      else twoCol(label, money(ex.total), width).forEach((l) => line(l));
    });
    if (it.note) wrap(`  Not: ${it.note}`, width).forEach((l) => line(l));
  });
  rule();

  if (r.discount) twoCol("İndirim", `-${money(r.discount)}`, width).forEach((l) => line(l));
  push(CMD.boldOn, CMD.sizeTall);
  twoCol("TOPLAM", money(r.total), width).forEach((l) => line(l));
  push(CMD.sizeNormal, CMD.boldOff);
  if (r.paymentMethod) line(`Ödeme: ${r.paymentMethod}`);
  rule();

  if (r.customerName) line(`Müşteri: ${r.customerName}`);
  if (r.customerPhone) line(`Tel: ${r.customerPhone}`);
  if (r.address) wrap(`Adres: ${r.address}`, width).forEach((l) => line(l));
  if (r.orderNote) { push(CMD.boldOn); wrap(`Sipariş Notu: ${r.orderNote}`, width).forEach((l) => line(l)); push(CMD.boldOff); }

  push(CMD.alignCenter);
  line();
  line(r.footer || "Afiyet olsun!");
  if (r.mapUrl) {
    line();
    wrap("Adres konumu için okutun", width).forEach((l) => line(l));
    push(qrCode(r.mapUrl, width === 32 ? 5 : 6));
  }
  push(CMD.feed(4));
  if (o.cut) push(CMD.cut);
  return Uint8Array.from(bytes);
}

export function toBase64(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode(...Array.from(bytes.subarray(i, i + 0x8000)));
  }
  return btoa(bin);
}

export const TEST_RECEIPT: ReceiptData = {
  restaurantName: "Test Fişi",
  orderId: "TEST-0001",
  orderDate: new Date().toISOString(),
  items: [
    { name: "Türkçe karakter: ç ğ ı İ ö ş ü Ç Ğ Ö Ş Ü", qty: 1, price: 0 },
    { name: "Kuyu Kebabı", qty: 2, price: 155, extras: [{ name: "Ekstra soğan", totalQty: 2, total: 10 }], note: "Acısız olsun" },
  ],
  total: 320,
  paymentMethod: "Kapıda Nakit",
  footer: "Bu satırları düzgün okuyabiliyorsanız ayarlar doğru.",
  mapUrl: "https://www.google.com/maps?q=35.1856,33.3823", // QR kodu desteğini de dener (Lefkoşa)
};

/** Android'de mi (RawBT önerisi için) */
export const isAndroid = () => typeof navigator !== "undefined" && /android/i.test(navigator.userAgent);

/**
 * Fişi RawBT uygulamasına verir. Uygulama kurulu değilse Android Play Store'a yönlendirir
 * (intent'teki browser_fallback_url).
 */
export function printViaRawBT(bytes: Uint8Array) {
  const fallback = encodeURIComponent("https://play.google.com/store/apps/details?id=ru.a402d.rawbtprinter");
  window.location.href = `intent:base64,${toBase64(bytes)}#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;S.browser_fallback_url=${fallback};end;`;
}
